import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import UniformForm from '@/components/UniformForm';
import { submitGroupRequest } from '@/services/brevoEmailService';
import { ScrollReveal } from '@/components/ScrollReveal';
import { Users, Calendar, MapPin, Award } from 'lucide-react';

const DemandeGroupe = () => {
  const formFields = [
    {
      id: 'nom',
      label: 'Nom',
      type: 'text' as const,
      required: true
    },
    {
      id: 'prenom',
      label: 'Prénom',
      type: 'text' as const,
      required: true
    },
    {
      id: 'email',
      label: 'Email',
      type: 'email' as const,
      required: true
    },
    {
      id: 'telephone',
      label: 'Téléphone',
      type: 'tel' as const,
      required: true
    },
    {
      id: 'entreprise',
      label: 'Entreprise/Organisation',
      type: 'text' as const,
      required: true
    },
    {
      id: 'fonction',
      label: 'Votre fonction',
      type: 'text' as const,
      placeholder: 'ex: Responsable RH, Organisateur événement...'
    },
    {
      id: 'typeEvenement',
      label: 'Type d\'événement',
      type: 'select' as const,
      required: true,
      placeholder: 'Sélectionnez le type...',
      options: [
        { value: 'seminaire', label: 'Séminaire d\'entreprise' },
        { value: 'team-building', label: 'Team building' },
        { value: 'sortie-associative', label: 'Sortie associative' },
        { value: 'evenement-prive', label: 'Événement privé' },
        { value: 'formation', label: 'Formation/Stage' },
        { value: 'autre', label: 'Autre' }
      ]
    },
    {
      id: 'nombrePersonnes',
      label: 'Nombre de participants',
      type: 'text' as const,
      required: true,
      placeholder: 'ex: 15 personnes'
    },
    {
      id: 'zone',
      label: 'Zone souhaitée',
      type: 'select' as const,
      required: true,
      placeholder: 'Sélectionnez la zone...',
      options: [
        { value: 'ile-de-re', label: 'Île de Ré' },
        { value: 'la-rochelle', label: 'La Rochelle' },
        { value: 'montalivet', label: 'Montalivet' },
        { value: 'royan', label: 'Royan' },
        { value: 'autre', label: 'Autre (à préciser dans le message)' }
      ]
    },
    {
      id: 'dateEvenement',
      label: 'Date souhaitée',
      type: 'date' as const,
      required: true
    },
    {
      id: 'duree',
      label: 'Durée',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'demi-journee', label: 'Demi-journée' },
        { value: 'journee', label: 'Journée complète' },
        { value: 'plusieurs-jours', label: 'Plusieurs jours' },
        { value: 'semaine', label: 'Une semaine' }
      ]
    },
    {
      id: 'typeVelo',
      label: 'Type de vélos souhaités',
      type: 'select' as const,
      options: [
        { value: 'classique', label: 'Vélos classiques' },
        { value: 'electrique', label: 'Vélos électriques' },
        { value: 'mixte', label: 'Mixte (classiques + électriques)' },
        { value: 'indifferent', label: 'Indifférent' }
      ]
    },
    {
      id: 'services',
      label: 'Services complémentaires',
      type: 'select' as const,
      placeholder: 'Sélectionnez si besoin...',
      options: [
        { value: 'accompagnement', label: 'Accompagnement/Guide' },
        { value: 'parcours', label: 'Création de parcours sur-mesure' },
        { value: 'materiel', label: 'Matériel supplémentaire (casques, paniers...)' },
        { value: 'livraison', label: 'Livraison sur site' },
        { value: 'aucun', label: 'Aucun service complémentaire' }
      ]
    },
    {
      id: 'budget',
      label: 'Budget approximatif',
      type: 'select' as const,
      placeholder: 'Indiquez votre budget...',
      options: [
        { value: 'moins-500', label: 'Moins de 500€' },
        { value: '500-1000', label: '500€ - 1000€' },
        { value: '1000-2000', label: '1000€ - 2000€' },
        { value: 'plus-2000', label: 'Plus de 2000€' },
        { value: 'non-defini', label: 'Budget non défini' }
      ]
    },
    {
      id: 'message',
      label: 'Message complémentaire',
      type: 'textarea' as const,
      placeholder: 'Décrivez votre projet, vos attentes, contraintes particulières...',
      rows: 4
    }
  ];

  const handleSubmit = async (data: Record<string, string>) => {
    await submitGroupRequest(data, data.email);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="h-8 w-8" />
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold mb-6">
            Groupes & Séminaires
          </h1>
          <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
            Organisez des activités vélo originales pour vos événements d'entreprise et sorties de groupe.
          </p>
        </div>
      </section>

      {/* Formulaire - Section prioritaire */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Demandez votre devis personnalisé
              </h2>
              <p className="text-slate-600">
                Nous créons des solutions sur-mesure pour vos événements de groupe.
              </p>
            </div>
            
            <UniformForm
              title="Demande groupe/séminaire"
              description="Tous les champs marqués d'un * sont obligatoires"
              fields={formFields}
              submitText="Envoyer ma demande de devis"
              onSubmit={handleSubmit}
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Nos services pour groupes */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Ce que nous proposons
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ScrollReveal delay={100}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Groupes 5-50 personnes
                </h3>
                <p className="text-slate-600 text-sm">
                  Solutions adaptées à la taille de votre groupe.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Parcours sur-mesure
                </h3>
                <p className="text-slate-600 text-sm">
                  Création d'itinéraires adaptés à vos objectifs.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Flexibilité totale
                </h3>
                <p className="text-slate-600 text-sm">
                  Demi-journée, journée complète ou plusieurs jours.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Accompagnement pro
                </h3>
                <p className="text-slate-600 text-sm">
                  Guide expérimenté et support technique inclus.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default DemandeGroupe;